import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getBlacklistAPI,
  addToBlacklistAPI,
  removeFromBlacklistAPI,
} from "../../lib/api/api";

export const fetchBlacklist = createAsyncThunk(
  "blacklist/fetchBlacklist",
  async (_, { dispatch }) => {
    const response = await getBlacklistAPI();
    if (response?.status === "unauthorized") {
      dispatch(setBlacklistAuthStatus(response.status));
    }
    return response;
  }
);

export const addToBlacklist = createAsyncThunk(
  "blacklist/addToBlacklist",
  async (asins, { dispatch }) => {
    const response = await addToBlacklistAPI(asins);
    if (response?.status === "unauthorized") {
      dispatch(setBlacklistAuthStatus(response.status));
    }
    return response;
  }
);

export const removeFromBlacklist = createAsyncThunk(
  "blacklist/removeFromBlacklist",
  async (asins, { dispatch }) => {
    const response = await removeFromBlacklistAPI(asins);
    if (response?.status === "unauthorized") {
      dispatch(setBlacklistAuthStatus(response.status));
    }
    return response;
  }
);

const initialFilters = {
  search: "",
  title: "",
  brand: "",
  category: "",
  bsrMin: "",
  bsrMax: "",
};

const blacklistSlice = createSlice({
  name: "blacklist",
  initialState: {
    items: [],
    status: "idle",
    error: null,
    authStatus: null,
    totalItems: 0,
    maxBlacklist: 100000,
    filters: { ...initialFilters },
    selected: [],
  },
  reducers: {
    setBlacklistAuthStatus: (state, action) => {
      state.authStatus = action.payload;
    },
    setBlacklistFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    resetBlacklistFilters: (state) => {
      state.filters = { ...initialFilters };
    },
    toggleBlacklistSelected: (state, action) => {
      const asin = action.payload;
      const idx = state.selected.indexOf(asin);
      if (idx >= 0) state.selected.splice(idx, 1);
      else state.selected.push(asin);
    },
    setBlacklistSelected: (state, action) => {
      state.selected = action.payload;
    },
    clearBlacklistSelected: (state) => {
      state.selected = [];
    },
    removeBlacklistAsinsLocally: (state, action) => {
      const removed = new Set(action.payload);
      state.items = state.items.filter((it) => !removed.has(it.asin));
      state.selected = state.selected.filter((a) => !removed.has(a));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlacklist.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchBlacklist.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload?.status === "authorized") {
          state.items = action.payload.data || [];
          state.totalItems = action.payload.total_count || 0;
          state.maxBlacklist =
            action.payload.max_blacklist || state.maxBlacklist;
        }
      })
      .addCase(fetchBlacklist.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(addToBlacklist.fulfilled, (state, action) => {
        if (action.payload?.status === "authorized") {
          state.totalItems =
            action.payload.blacklist_count ?? state.totalItems;
        }
      })
      .addCase(removeFromBlacklist.fulfilled, (state, action) => {
        if (action.payload?.status === "authorized") {
          state.totalItems =
            action.payload.blacklist_count ?? state.totalItems;
          state.selected = [];
        }
      });
  },
});

export const {
  setBlacklistAuthStatus,
  setBlacklistFilter,
  resetBlacklistFilters,
  toggleBlacklistSelected,
  setBlacklistSelected,
  clearBlacklistSelected,
  removeBlacklistAsinsLocally,
} = blacklistSlice.actions;
export default blacklistSlice.reducer;
